/**
 * LocationAvailabilityField (DNKADM-0137 U1) — the shared **"Available at"** control
 * for the Plan and Package edit dialogs (DNKAPI-0253/0254).
 *
 * A plan/package is available at a set of owned locations, with the semantic **empty ⇒
 * ALL locations**. That is the OPPOSITE of the Services location-assignment control
 * (`AssignmentEditor` — there an empty set means the service is offered NOWHERE / is
 * *excluded* under a location filter). So this is a DEDICATED toggle, NOT a repurposed
 * `AssignmentEditor`: a two-way choice between **"All locations"** (the default, and
 * whenever `value` is `[]`) and **"Specific locations"** (a multi-select of the tenant's
 * active locations). Only the multi-select's rendering mechanics (checkbox rows + chips)
 * are borrowed from `AssignmentEditor`; the meaning of empty is deliberately inverted.
 *
 * Loads its own active-location roster (`listLocations()` via `useResource`, once on
 * mount — each dialog mounts this fresh per open). A load failure degrades to a calm
 * inline note + Retry and MUST NOT block the rest of the dialog's fields (Name/Price/…
 * don't depend on this control loading).
 */
import { useCallback, useId, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Skeleton,
} from '@mui/material';
import { useResource } from '@/hooks/useResource';
import { listLocations } from '@/api/locations';

export interface LocationAvailabilityFieldProps {
  /** Selected location ids; `[]` means "all locations". */
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

type Mode = 'all' | 'specific';

export function LocationAvailabilityField({
  value,
  onChange,
  disabled = false,
}: LocationAvailabilityFieldProps) {
  const ids = useId();
  // Local toggle state so "Specific locations" can be active with an empty selection
  // (so the picker opens for a first choice). Seeded from `value`: a preset selection
  // starts in "Specific", an empty one in "All". Switching to "All" clears to `[]`.
  const [mode, setMode] = useState<Mode>(value.length > 0 ? 'specific' : 'all');

  const locations = useResource(
    useCallback(() => listLocations(), []),
    [],
  );

  function handleModeChange(next: Mode) {
    if (next === 'all') {
      setMode('all');
      onChange([]);
    } else {
      setMode('specific');
    }
  }

  const labelId = `${ids}-label`;
  const selectLabelId = `${ids}-select-label`;
  const options = locations.data ?? [];

  return (
    <FormControl component="fieldset" disabled={disabled} sx={{ width: '100%' }}>
      <FormLabel id={labelId} sx={{ typography: 'body2', mb: 0.5 }}>
        Available at
      </FormLabel>
      <RadioGroup
        row
        aria-labelledby={labelId}
        value={mode}
        onChange={(e) => handleModeChange(e.target.value as Mode)}
      >
        <FormControlLabel value="all" control={<Radio size="small" />} label="All locations" />
        <FormControlLabel
          value="specific"
          control={<Radio size="small" />}
          label="Specific locations"
        />
      </RadioGroup>

      {mode === 'specific' && (
        <Box sx={{ mt: 1 }}>
          {locations.status === 'loading' && (
            <Skeleton
              variant="rounded"
              height={40}
              role="status"
              aria-label="Loading locations"
            />
          )}

          {locations.status === 'error' && (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={locations.refetch}>
                  Retry
                </Button>
              }
              sx={{ py: 0.5 }}
            >
              Couldn’t load locations — try again.
            </Alert>
          )}

          {locations.status === 'ready' && (
            <FormControl size="small" fullWidth disabled={disabled || options.length === 0}>
              <InputLabel id={selectLabelId}>Locations</InputLabel>
              <Select
                labelId={selectLabelId}
                multiple
                value={value}
                onChange={(e) =>
                  onChange(
                    typeof e.target.value === 'string'
                      ? e.target.value.split(',')
                      : e.target.value,
                  )
                }
                input={<OutlinedInput label="Locations" />}
                slotProps={{ input: { 'aria-label': 'Locations' } }}
                renderValue={(picked) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {picked.map((id) => {
                      const opt = options.find((o) => o.id === id);
                      return (
                        <Chip key={id} size="small" label={opt ? opt.name || opt.id : id} />
                      );
                    })}
                  </Box>
                )}
              >
                {options.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    <Checkbox checked={value.includes(opt.id)} size="small" />
                    <ListItemText primary={opt.name === '' ? opt.id : opt.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {locations.status === 'ready' && options.length === 0 && (
            <Alert severity="info" sx={{ py: 0.5, mt: 1 }}>
              No locations to choose yet — the plan stays available everywhere.
            </Alert>
          )}
        </Box>
      )}
    </FormControl>
  );
}
